import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useSelector } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { formValues, schema } from "./validations";
import {
  SOURCE_SYSTEM,
  DOCUMENT_TYPE,
  statusOptions,
  sourceSystemOptions,
  SEARCH_VALUE,
  DATE_RANGE,
  DATE_FROM,
  DATE_TO,
  quickInsightsBaseData,
  STAGE,
} from "./consts";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAuditTrailCardsData, useGetAuditTrailGrid, useGetDocumentTypeDropdown } from "@/services";
import moment from "moment";
import { processingStatusList, toApiSort } from "@/lib/utils";

const useAuditTrail = () => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "startedAt", desc: true }]);
  const [isSubmitClicked, setIsSubmittedClicked] = useState<boolean>(false);
  const { communityData } = useSelector(
    (state: RootState) => state.currentData
  );

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [SOURCE_SYSTEM]: "all",
      [STAGE]: "SUBMISSION",
      [DOCUMENT_TYPE]: "",
      [SEARCH_VALUE]: '',
      [DATE_RANGE]: {from: null, to: null}
    }
  });
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.audit}`,
        name: <span className="text-primary">{"Audit"}</span>,
      },
      {
        link: "",
        name: <span className="text-primary">{"Audit Trail"}</span>,
      },
    ],
    []
  );

  const watchedSearchValue = useWatch({control: form.control, name: SEARCH_VALUE});
  const formWatched = useWatch({control: form.control});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const onViewSubmission = (row: any) => {
    navigate(
      `/${routes.bom}/${routes.finance}/${routes.audit}/${routes.auditTrail}/${row.jeMasterId}/${routes.view}`,
      { state: { stage: formWatched[STAGE], ujeAuditId: row?.ujeAuditId }}
    );
  };

  const submissionsColumns = [
    {
      accessorKey: "startedAt",
      header: <div>Submission Time</div>,
      accessorFn: (row: any) => row.startedAt ? moment.utc(row?.startedAt).local().format("MM/DD/YYYY HH:mm:ss") : "",
    },
    {
      accessorKey: "displayId",
      header: <div>Entry ID</div>,
      cell: ({row}: any) => (
        <span className="text-primary font-semibold">{row.original.displayId}</span>
      ),
    },
    {
      accessorKey: "sourceSystem",
      header: <div>Source System</div>,
      accessorFn: (row: any) => sourceSystemOptions?.find((data: any) => data?.value == row?.sourceSystem)?.label || "",
    },
    {
      accessorKey: "documentType",
      header: <div>Document Type</div>,
      accessorFn: (row: any) => String(row?.documentType || ""),
    },
    // {
    //   accessorKey: "sourceReferenceId",
    //   header: <div>Source Reference</div>,
    //   accessorFn: (row: any) => String(row?.sourceReferenceId || ""),
    // },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        return (
          <div className={processingStatusList[row.original.status]?.className}>
            {processingStatusList[row.original.status]?.label}
          </div>
        )
      },
    },
    // {
    //   accessorKey: "durationMs",
    //   header: <div>Processing<br/>Time</div>,
    //   accessorFn: (row: any) => row?.startedAt && row?.completedAt ? moment(row.completedAt).diff(row.startedAt, "milliseconds") : "",
    // },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({row}: any) => (
        <button
          type="button"
          className="w-[71px] h-[40px] rounded-[8px] border border-primary px-3 text-primary"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onViewSubmission(row.original);
          }}
        >
          View
        </button>
      ),
    },
  ];

  const {data: documentTypeList, isLoading: isListLoading} = useGetDocumentTypeDropdown({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  })

  const VIDocumentTypeId = useMemo(() => documentTypeList?.find((data: any) => data?.documentType == "VI")?.documentTypeId, [documentTypeList])
  const OBDocumentTypeId = useMemo(() => documentTypeList?.find((data: any) => data?.documentType == "OB")?.documentTypeId, [documentTypeList])

  const {data: filterData, isRefetching: isLoadingData, refetch: refetchFilterData} = useGetAuditTrailCardsData({
    communityId: communityData?.communityId,
    data: {
      ...(formWatched[SOURCE_SYSTEM] != "all" ? {[SOURCE_SYSTEM]: formWatched?.[SOURCE_SYSTEM] } : {}),
      ...(formWatched[DOCUMENT_TYPE] ? {[DOCUMENT_TYPE]: formWatched?.[DOCUMENT_TYPE] } : {}),
      // [STAGE]: formWatched?.[STAGE],
      [DATE_FROM]: formWatched?.[DATE_RANGE]?.from,
      [DATE_TO]: formWatched?.[DATE_RANGE]?.to,
    },
    // queryParams: {
    //   enabled: false
    // }
  })

  const {data: submissionList, isRefetching: isSubmissionListRefetching, refetch: refetchSubmissionList} = useGetAuditTrailGrid({
    communityId: communityData?.communityId,
    data: {
      ...(formWatched[SOURCE_SYSTEM] != "all" ? {[SOURCE_SYSTEM]: formWatched?.[SOURCE_SYSTEM] } : {}),
      ...(formWatched[DOCUMENT_TYPE] ? {[DOCUMENT_TYPE]: formWatched?.[STAGE] == "SUBLEDGER_VENDOR" ? VIDocumentTypeId : formWatched?.[STAGE] == "SUBLEDGER_CUSTOMER" ? OBDocumentTypeId : formWatched?.[DOCUMENT_TYPE] } : {}),
      [STAGE]: formWatched?.[STAGE],
      [DATE_FROM]: formWatched?.[DATE_RANGE]?.from,
      [DATE_TO]: formWatched?.[DATE_RANGE]?.to,
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "startedAt", direction: "DESC" }),
    },
    // queryParams: {
    //   enabled: false
    // }
  })

  const onClearAll = () => {
    form.reset({
      [SOURCE_SYSTEM]: "all",
      [STAGE]: "SUBMISSION",
      [DOCUMENT_TYPE]: "",
      [SEARCH_VALUE]: '',
      [DATE_RANGE]: {startDate: null, endDate: null}
    });
  };

  const onSubmit = () => {
    setIsSubmittedClicked(true);
    refetchFilterData();
    refetchSubmissionList();
  }

  useEffect(() => {
    refetchSubmissionList();
  }, [pageNumber, pageSize, sorting]);

  const quickInsightsData = useMemo(() => {
    return quickInsightsBaseData?.map((data: any) => {
      const _data = filterData?.data?.find((fData: any) => fData?.stage?.toLowerCase() == data?.stage?.toLowerCase())
      return {
        ...data, 
        data: {
          ...(data.hasSplit ? 
            {
              passed: {
              label: data?.passedLabel ? data.passedLabel : "Passed",
              value: _data?.passed
              },
              failed: {
                label: data?.failedLabel ? data.failedLabel : "Failed",
                value: _data?.failed
              }
          } : {}),
          total: {
            label: "Total",
            value: _data?.[STAGE] == "SUBLEDGER_VENDOR" && OBDocumentTypeId == formWatched[DOCUMENT_TYPE] ? 0 : _data?.[STAGE] == "SUBLEDGER_CUSTOMER" && VIDocumentTypeId == formWatched[DOCUMENT_TYPE] ? 0 : _data?.total
          }
        }
      }
    })
  }, [filterData])

  const onCardClick = (stage: string) => () => {
    form.setValue(STAGE, stage);

    if ((stage == "SUBLEDGER_VENDOR" && OBDocumentTypeId == formWatched[DOCUMENT_TYPE]) || (stage == "SUBLEDGER_CUSTOMER" && VIDocumentTypeId == formWatched[DOCUMENT_TYPE])) {
      return;
    }
    setTimeout(() => refetchSubmissionList(), 100)
  }

  const gridData = useMemo(() => {
    if (formWatched?.[STAGE] == "SUBLEDGER_VENDOR" && OBDocumentTypeId == formWatched[DOCUMENT_TYPE]) {
      return [];
    } else if (formWatched?.[STAGE] == "SUBLEDGER_CUSTOMER" && VIDocumentTypeId == formWatched[DOCUMENT_TYPE]) {
      return [];
    } else if (submissionList?.data) {
      return submissionList?.data
    } else {
      return []
    }
  }, [submissionList, formWatched?.[STAGE]]);

  return {
    breadcrumbData,
    communityData,
    form,
    statusOptions,
    sourceSystemOptions,
    submissionsColumns,
    watchedSearchValue,
    onClearAll,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    setPageNumber,
    setPageSize,
    onSubmit,
    isLoadingData,
    isListLoading,
    documentTypeList,
    quickInsightsData,
    isSubmissionListRefetching,
    submissionList: submissionList?.data,
    stageValue: formWatched[STAGE],
    onCardClick,
    documentType: formWatched[DOCUMENT_TYPE],
    isSubmitClicked,
    gridData
  };
};

export default useAuditTrail;
